import { BaseApi } from './base.api.js';
import { teamAssociationApiMethods } from './team-association.mixin.js';

export class CrisisEventApi extends BaseApi {
    constructor(client) {
        super(client, '/crisis-event');
        Object.assign(this, teamAssociationApiMethods(client, this.basePath));
    }

    async getStats() {
        const response = await this.client.get(`${this.basePath}/stats`);
        return response.data;
    }

    // --- Activated BCPs Relation Methods ---

    async getRelatedBcps(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/bcps`, { params });
        return response.data;
    }

    async addBcps(id, bcpIds) {
        const response = await this.client.post(`${this.basePath}/${id}/bcps`, { bcpIds });
        return response.data;
    }

    async removeBcp(id, bcpId) {
        const response = await this.client.delete(`${this.basePath}/${id}/bcps/${bcpId}`);
        return response.data;
    }

    async getRelatedBias(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/bias`, { params });
        return response.data;
    }

    async addBias(id, biaIds) {
        const response = await this.client.post(`${this.basePath}/${id}/bias`, { biaIds });
        return response.data;
    }

    async removeBia(id, biaId) {
        const response = await this.client.delete(`${this.basePath}/${id}/bias/${biaId}`);
        return response.data;
    }

    // --- Processes ---
    async getRelatedProcesses(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/processes`, { params });
        return response.data;
    }
    async addProcesses(id, processIds) {
        const response = await this.client.post(`${this.basePath}/${id}/processes`, { processIds });
        return response.data;
    }
    async removeProcess(id, processId) {
        const response = await this.client.delete(`${this.basePath}/${id}/processes/${processId}`);
        return response.data;
    }

    // --- Assets ---
    async getRelatedAssets(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/assets`, { params });
        return response.data;
    }
    async addAssets(id, assetIds) {
        const response = await this.client.post(`${this.basePath}/${id}/assets`, { assetIds });
        return response.data;
    }
    async removeAsset(id, assetId) {
        const response = await this.client.delete(`${this.basePath}/${id}/assets/${assetId}`);
        return response.data;
    }

    // --- Vendors ---
    async getRelatedVendors(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/vendors`, { params });
        return response.data;
    }
    async addVendors(id, vendorIds) {
        const response = await this.client.post(`${this.basePath}/${id}/vendors`, { vendorIds });
        return response.data;
    }
    async removeVendor(id, vendorId) {
        const response = await this.client.delete(`${this.basePath}/${id}/vendors/${vendorId}`);
        return response.data;
    }

    // --- Communications ---
    async getRelatedCommunications(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/communications`, { params });
        return response.data;
    }
    async addCommunications(id, communicationIds) {
        const response = await this.client.post(`${this.basePath}/${id}/communications`, { communicationIds });
        return response.data;
    }
    async removeCommunication(id, communicationId) {
        const response = await this.client.delete(`${this.basePath}/${id}/communications/${communicationId}`);
        return response.data;
    }

    // --- Communication Plans ---
    async getRelatedCommunicationPlans(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/communication-plans`, { params });
        return response.data;
    }
    async addCommunicationPlans(id, planIds) {
        const response = await this.client.post(`${this.basePath}/${id}/communication-plans`, { planIds });
        return response.data;
    }
    async removeCommunicationPlan(id, planId) {
        const response = await this.client.delete(`${this.basePath}/${id}/communication-plans/${planId}`);
        return response.data;
    }

    // --- Recovery Resources ---
    async getRelatedRecoveryResources(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/recovery-resources`, { params });
        return response.data;
    }
    async addRecoveryResources(id, resourceIds) {
        const response = await this.client.post(`${this.basePath}/${id}/recovery-resources`, { resourceIds });
        return response.data;
    }
    async removeRecoveryResource(id, resourceId) {
        const response = await this.client.delete(`${this.basePath}/${id}/recovery-resources/${resourceId}`);
        return response.data;
    }

    // --- Recovery Activities ---
    async getRelatedRecoveryActivities(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/recovery-activities`, { params });
        return response.data;
    }
    async addRecoveryActivities(id, activityIds) {
        const response = await this.client.post(`${this.basePath}/${id}/recovery-activities`, { activityIds });
        return response.data;
    }
    async removeRecoveryActivity(id, activityId) {
        const response = await this.client.delete(`${this.basePath}/${id}/recovery-activities/${activityId}`);
        return response.data;
    }

    async reorderRecoveryActivities(id, orderedActivityIds) {
        const response = await this.client.patch(`${this.basePath}/${id}/recovery-activities/order`, { orderedActivityIds });
        return response.data;
    }

    // --- Lessons Learned ---
    async getRelatedLessonsLearned(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/lessons-learned`, { params });
        return response.data;
    }
    async addLessonsLearned(id, lessonIds) {
        const response = await this.client.post(`${this.basePath}/${id}/lessons-learned`, { lessonIds });
        return response.data;
    }
    async removeLessonLearned(id, lessonId) {
        const response = await this.client.delete(`${this.basePath}/${id}/lessons-learned/${lessonId}`);
        return response.data;
    }

    // --- Improvement Actions ---
    async getRelatedImprovementActions(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/improvement-actions`, { params });
        return response.data;
    }
    async getLinkableImprovementActions(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/improvement-actions/linkable`, { params });
        return response.data;
    }
    async addImprovementActions(id, actionIds, extra = {}) {
        const response = await this.client.post(`${this.basePath}/${id}/improvement-actions`, { actionIds, ...extra });
        return response.data;
    }
    async unlinkImprovementActions(id, actionIds) {
        const response = await this.client.post(`${this.basePath}/${id}/improvement-actions/unlink`, { actionIds });
        return response.data;
    }
    async removeImprovementAction(id, actionId) {
        const response = await this.client.delete(`${this.basePath}/${id}/improvement-actions/${actionId}`);
        return response.data;
    }

    async getPartyTemplate(params = {}) {
        const response = await this.client.get(`${this.basePath}/party-template`, { params });
        return response.data;
    }

    async getParty(id) {
        const response = await this.client.get(`${this.basePath}/${id}/party`);
        return response.data;
    }

    async syncParty(id, body) {
        const response = await this.client.put(`${this.basePath}/${id}/party`, body);
        return response.data;
    }

    async activateBcp(id, bcpId) {
        const response = await this.client.post(`${this.basePath}/${id}/activate-bcp`, { bcpId });
        return response.data;
    }
}
